import { db } from "@/lib/firebase";
import {
  addDoc,
  and,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  or,
  orderBy,
  query,
  updateDoc,
  where
} from "firebase/firestore";

export interface CaseStudySection {
    id: string; // unique id for key
    type: 'text' | 'header' | 'divider' | 'media' | 'video';
    content: string; // text content or url
}

export interface CaseStudy {
    id: string;
    title: string;
    category: string;
    coverImage: string;
    description: string; // Keeping for backward compatibility or short summary if needed, but UI might prioritize sections

    // New Fields
    status: "Draft" | "Published" | "Archived";
    websiteLink?: string;
    playStoreLink?: string;
    appStoreLink?: string;
    uiDesignLink?: string;
    tags: string[];
    duration?: number; // months
    excerpt?: string;
    problemStatement?: string;
    solution?: string;
    isFeatured?: boolean;
    metrics?: {
        label: string;
        value: string;
        icon: string;
    }[];

    // Tech Stack
    techStack?: {
        name: string;
        icon: string; // can be Lucide name or Image URL
    }[];

    // Testimonial
    testimonial?: {
        quote: string;
        author: string;
        role: string;
        avatar?: string;
    };

    // Timeline
    timeline?: {
        date: string;
        title: string;
        description: string;
    }[];

    // List of authors with roles
    team?: {
        id: string;
        role: string;
    }[];

    sections: CaseStudySection[];

    createdAt: number;
}

const COLLECTION_NAME = "case-studies";

export async function getCaseStudies() {
    try {
        const q = query(collection(db, COLLECTION_NAME), orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        })) as CaseStudy[];

        return { success: true, data };
    } catch (error) {
        console.error("Error fetching case studies:", error);
        return { success: false, error: "Failed to fetch case studies" };
    }
}

export async function getCaseStudyById(id: string) {
    try {
        const docRef = doc(db, COLLECTION_NAME, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return { success: true, data: { id: docSnap.id, ...docSnap.data() } as CaseStudy };
        } else {
            return { success: false, error: "Case study not found" };
        }
    } catch (error) {
        console.error("Error fetching case study:", error);
        return { success: false, error: "Failed to fetch case study" };
    }
}

export async function createCaseStudy(data: Omit<CaseStudy, "id" | "createdAt">) {
    try {
        const newCaseStudy = {
            ...data,
            createdAt: Date.now(),
        };

        const docRef = await addDoc(collection(db, COLLECTION_NAME), newCaseStudy);
        return { success: true, data: { id: docRef.id, ...newCaseStudy } };
    } catch (error) {
        console.error("Error creating case study:", error);
        return { success: false, error: "Failed to create case study" };
    }
}

export async function updateCaseStudy(id: string, data: Partial<CaseStudy>) {
    try {
        const docRef = doc(db, COLLECTION_NAME, id);
        await updateDoc(docRef, data);
        return { success: true };
    } catch (error) {
        console.error("Error updating case study:", error);
        return { success: false, error: "Failed to update case study" };
    }
}

export async function deleteCaseStudy(id: string) {
    try {
        await deleteDoc(doc(db, COLLECTION_NAME, id));
        return { success: true };
    } catch (error) {
        console.error("Error deleting case study:", error);
        return { success: false, error: "Failed to delete case study" };
    }
}

export async function getFeaturedCaseStudies() {
    try {
        const q = query(
            collection(db, COLLECTION_NAME),
            where("isFeatured", "==", true),
            where("status", "==", "Published"),
            orderBy("createdAt", "desc")
        );
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        })) as CaseStudy[];

        return { success: true, data };
    } catch (error) {
        console.error("Error fetching featured case studies:", error);
        return { success: false, error: "Failed to fetch featured case studies" };
    }
}

export async function getPublishedCaseStudies(category?: string, lastCreatedAt?: number, limitCount: number = 10) {
    try {
        const filters: any[] = [
            where("status", "==", "Published")
        ];

        if (category && category !== "All") {
            filters.push(
                or(
                    where("category", "==", category),
                    where("tags", "array-contains", category)
                )
            );
        }

        if (lastCreatedAt) {
            filters.push(where("createdAt", "<", lastCreatedAt));
        }

        let q;
        if (filters.length > 1) {
            q = query(
                collection(db, COLLECTION_NAME),
                and(...filters),
                orderBy("createdAt", "desc"),
                limit(limitCount)
            );
        } else {
            q = query(
                collection(db, COLLECTION_NAME),
                filters[0],
                orderBy("createdAt", "desc"),
                limit(limitCount)
            );
        }
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        })) as CaseStudy[];

        return { success: true, data };
    } catch (error) {
        console.error("Error fetching published case studies:", error);
        return { success: false, error: "Failed to fetch published case studies" };
    }
}
export async function getRelatedCaseStudies(currentId: string, category: string, limitCount: number = 3) {
    try {
        const q = query(
            collection(db, COLLECTION_NAME),
            where("status", "==", "Published"),
            // where("category", "==", category), // Removed to avoid index requirement for now, filtering client side or just recent
            orderBy("createdAt", "desc"),
            limit(10)
        );
        const snapshot = await getDocs(q);
        let data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        })) as CaseStudy[];

        // Filter out current
        data = data.filter(item => item.id !== currentId);

        // Filter by category if possible (safely)
        const sameCategory = data.filter(item => item.category === category);

        // Return same category if enough, else mixed
        if (sameCategory.length >= 1) {
            data = sameCategory; // Prioritize same category
        }


        return { success: true, data: data.slice(0, limitCount) };
    } catch (error) {
        console.error("Error fetching related case studies:", error);
        return { success: false, error: "Failed to fetch related case studies" };
    }
}

export async function getCaseStudyTestimonials() {
    try {
        const q = query(
            collection(db, COLLECTION_NAME),
            where("status", "==", "Published"),
            orderBy("createdAt", "desc")
        );
        const snapshot = await getDocs(q);
        
        const testimonials = snapshot.docs
            .map(doc => {
                const data = doc.data() as CaseStudy;
                return {
                    id: doc.id,
                    company: data.title, // Fallback to title if company name not explicit, though typically title is company name in many portfolios
                    ...data.testimonial
                };
            })
            .filter(t => t.quote && t.author); // Only return valid testimonials

        return { success: true, data: testimonials };
    } catch (error) {
        console.error("Error fetching testimonials:", error);
        return { success: false, error: "Failed to fetch testimonials" };
    }
}
